import React, { ReactNode, useCallback, useContext, useEffect, useRef, useState } from "react"

// ** Third Party Components
import axios from "axios"
import { Edit2 } from "lucide-react"
import { parseCookies } from "nookies"
import { useMutation } from "@tanstack/react-query"
import { useNavigate, useSearchParams } from "react-router-dom"

// ** UI Components
import { Textarea } from "@components/common/textarea"

// ** API Routes
import { EDIT_PROJECT_BY_CURRENT_USER_ENDPOINT, SERVER_URL } from "@pma/utils/api-routes"

// ** Types
import { DictType } from "@pma/types"

// ** Constants
import { ACCESS_TOKEN } from "@pma/utils/constants"

// ** Store
import { useAuth } from "@stores/auth-store"

// ** Helpers
import { cn } from "@pma/utils/helpers"

// ** Custom Context
import { ProjectContext } from "@pma/contexts/ProjectContext"

// ** Custom Hooks
import { toast } from "@hooks/use-toast"
import useTranslator from "@hooks/use-translator"

// ** Custom Components
import GeoCoder from "./geo-coder"
import DateEditor from "./date-editor"
import StatusEditor from "./project-status-editor"
import NeighborhoodSelector from "./neighbourhood-selector"

interface GeoLocation {
  name: string
  lat: number
  long: number
}

interface Props {
  id: number
  title: string
  children: ReactNode
  value: string | null | undefined
  refetch?: CallableFunction
  type?: "address" | "status" | "neighborhood" | "date"
  maxLength?: number
  root_folder?: DictType
  setIsLoading?: (loading: boolean) => void
}

const ProjectEditor = ({
  children,
  id,
  title,
  value,
  refetch,
  type,
  maxLength,
  root_folder,
  setIsLoading,
}: Props) => {
  const hasSubmittedRef = useRef(false)

  const navigate = useNavigate()
  const { authConfig } = useAuth()
  const { translate } = useTranslator()
  const [searchParams] = useSearchParams()
  const accessToken = parseCookies()[ACCESS_TOKEN]

  const userId = authConfig?.id
  const isAutoSubmitType = type === "neighborhood" || type === "address"

  const { setIsEditing } = useContext(ProjectContext)

  const [hover, setHover] = useState(false)
  const [editMode, setEditMode] = useState(false)
  const [geoLocation, setGeoLocation] = useState<GeoLocation | null>(null)
  const [updatedValue, setUpdatedValue] = useState<string | number>(value ?? "")

  const handleUpdateProject = async () => {
    let payload = { project_id: id, user_id: userId }
    let updates: Record<string, unknown>

    if (type === "address" && geoLocation) {
      updates = {
        [title]: geoLocation.name,
        point_lng: geoLocation.long,
        point_lat: geoLocation.lat,
      }
    } else if (type === "neighborhood") updates = { neighborhood_id: updatedValue }
    else updates = { [title]: updatedValue }

    const response = await axios.post(
      SERVER_URL + EDIT_PROJECT_BY_CURRENT_USER_ENDPOINT,
      { ...payload, ...updates },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      },
    )

    return response.data
  }

  const updateProjectDetailsMutation = useMutation({
    mutationKey: [`${title}`, id],
    mutationFn: handleUpdateProject,
    onMutate: () => {
      setEditMode(false)
      setIsEditing(false)
      if (type === "neighborhood") setIsLoading?.(true)
    },
    onSuccess: () => {
      if (type === "neighborhood") setIsLoading?.(false)

      if (refetch) refetch("project")
      else {
        const newParams = new URLSearchParams(searchParams)
        newParams.set("revalidate", "projects")
        navigate(`?${newParams.toString()}`)
      }

      hasSubmittedRef.current = false
    },
    onError: () => {
      if (type === "neighborhood") setIsLoading?.(false)
      hasSubmittedRef.current = false

      toast({
        title: translate("Error"),
        description: `${translate("Error occurred while updating")} ${title.replace("_", " ")}`,
        variant: "error",
      })
    },
  })

  const handleSubmit = useCallback(() => {
    if (hasSubmittedRef.current) return

    const hasValueChanged = updatedValue !== "" && updatedValue !== value
    const hasGeoChanged = type === "address" && geoLocation?.name

    if (hasValueChanged || hasGeoChanged) {
      hasSubmittedRef.current = true
      updateProjectDetailsMutation.mutate()
    } else {
      setEditMode(false)
      setIsEditing(false)
    }
  }, [updatedValue, value, type, geoLocation, updateProjectDetailsMutation, setIsEditing])

  // Sync internal state when prop changes
  useEffect(() => {
    setUpdatedValue(value ?? "")
  }, [value])

  // Auto-submit for neighborhood when value changes
  useEffect(() => {
    if (type === "neighborhood" && editMode && updatedValue !== value && updatedValue !== "")
      handleSubmit()
  }, [type, editMode, updatedValue, value, handleSubmit])

  // Auto-submit for address when geoLocation changes
  useEffect(() => {
    if (type === "address" && geoLocation?.name && editMode) handleSubmit()
  }, [type, geoLocation?.name, editMode, handleSubmit])

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setEditMode(true)
    setIsEditing(true)
    hasSubmittedRef.current = false
  }

  const handleBlur = () => {
    if (!isAutoSubmitType) handleSubmit()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !isAutoSubmitType) handleSubmit()
  }

  const renderEditor = () => {
    switch (type) {
      case "address":
        return <GeoCoder location={geoLocation?.name || ""} setGeoLocation={setGeoLocation} />
      case "status":
        return <StatusEditor status={{ name: value }} setStatus={setUpdatedValue} projectId={id} />
      case "date": {
        const projectDates = value as string
        const [startAt, finishTo] = projectDates?.split("_") ?? []
        return (
          <DateEditor value={{ from: parseInt(startAt), to: parseInt(finishTo) }} projectId={id} />
        )
      }
      case "neighborhood":
        return (
          <NeighborhoodSelector
            value={updatedValue}
            setValue={setUpdatedValue}
            projectId={id}
            setEditMode={setEditMode}
          />
        )
      default:
        return (
          <div className='flex flex-col'>
            {maxLength ? (
              <Textarea
                value={updatedValue as string}
                onChange={(e) => setUpdatedValue(e.target.value)}
                placeholder={`Edit ${title.replaceAll("_", " ")}`}
                maxLength={maxLength}
                className='w-full'
                cols={40}
                rows={3}
                onBlur={handleBlur}
              />
            ) : (
              <input
                className='mb-1 rounded border bg-white/20 px-2 font-normal outline-none focus:border-primary'
                value={updatedValue as string}
                onChange={(e) => setUpdatedValue(e.target.value)}
                autoFocus
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
              />
            )}
          </div>
        )
    }
  }

  return (
    <div
      className={cn(
        "hover:border-3 flex w-full cursor-pointer items-center justify-start gap-2 text-center font-semibold text-black hover:border-primary",
        updateProjectDetailsMutation.isPending &&
          "animate-shimmer bg-gradient-to-r from-black to-slate-500 bg-clip-text font-light text-transparent",
      )}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {!editMode ? (
        <div className='flex h-full w-full gap-2 text-left'>
          {children}
          <Edit2
            className={cn(
              "edit-icon hover:border-3 text-primary",
              hover ? "opacity-100" : "opacity-0",
            )}
            size={14}
            onClick={handleEditClick}
          />
        </div>
      ) : (
        renderEditor()
      )}
    </div>
  )
}

export default ProjectEditor
